import {
  X,
  Pencil,
  Building2,
  UserRound,
  Stethoscope,
  Phone,
  Mail,
  MapPin,
  Landmark,
  Hash,
  StickyNote,
  Calendar,
  Activity,
  TrendingUp,
  Wallet,
  Clock,
  Receipt,
  CheckCircle,
  type LucideIcon,
} from 'lucide-react';
import { useReferralSummary, useReferralPayouts } from '../../../services/httpServices/queries';
import { SectionHeader } from '../../../components/ui/modal-kit';
import { formatOrdinalDateTime } from '../../../utils/dateFormat';
import type { ReferralPartner } from '../../../services/httpServices/referralService';

interface ReferralPartnerDetailsModalProps {
  partner: ReferralPartner | null;
  onClose: () => void;
  /** Open the edit form for this partner. */
  onEdit?: (partner: ReferralPartner) => void;
}

function formatCurrency(value: number | string | undefined): string {
  const n = Number(value) || 0;
  return `৳${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function initials(name?: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** A labelled field with a small leading icon. */
function DetailItem({
  icon: Icon,
  label,
  value,
}: {
  icon: LucideIcon;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      <p className="mb-1 flex items-center gap-1.5 text-[0.6875rem] font-medium uppercase tracking-wide text-text-muted">
        <Icon size={12} className="shrink-0" />
        {label}
      </p>
      <p className="truncate text-sm text-text" title={typeof value === 'string' ? value : undefined}>
        {value}
      </p>
    </div>
  );
}

/** A compact KPI tile used in the performance grid. */
function StatTile({
  icon: Icon,
  label,
  value,
  accent = 'text-primary bg-primary/10',
}: {
  icon: LucideIcon;
  label: string;
  value: React.ReactNode;
  accent?: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-background p-3">
      <div className="flex items-center gap-2">
        <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${accent}`}>
          <Icon size={14} />
        </span>
        <span className="text-[0.6875rem] font-medium uppercase tracking-wide text-text-muted">{label}</span>
      </div>
      <p className="mt-2 text-lg font-bold text-text">{value}</p>
    </div>
  );
}

/**
 * Comprehensive, read-only profile for a referral partner — opened from the
 * registry tables. Combines the partner's identity/contact/bank details with a
 * live performance summary and recent payout history pulled from the summary
 * and payout endpoints.
 */
export function ReferralPartnerDetailsModal({
  partner,
  onClose,
  onEdit,
}: ReferralPartnerDetailsModalProps) {
  const partnerId = partner?.id ?? '';
  const isDoctor = partner?.type === 'DOCTOR';
  const isEmployee = partner?.type === 'EMPLOYEE';

  const { data: summaryRes, isLoading: summaryLoading } = useReferralSummary(partnerId, !!partnerId);
  const { data: payoutsRes, isLoading: payoutsLoading } = useReferralPayouts(partnerId, 1, 5, !!partnerId);

  if (!partner) return null;

  const summary = summaryRes?.data;
  const payouts = payoutsRes?.data ?? [];
  const payoutTotal = payoutsRes?.meta?.total ?? payouts.length;

  const totalEarned =
    (Number(summary?.pendingShare) || 0) +
    (Number(summary?.postedShare) || 0) +
    (Number(summary?.settledShare) || 0);

  const typeLabel = isDoctor ? 'Doctor' : isEmployee ? 'Employee' : 'Other Referrer';
  const TypeIcon = isDoctor ? Stethoscope : isEmployee ? UserRound : Building2;

  const hasBankDetails = !!(
    partner.bankName ||
    partner.bankAccountNumber ||
    partner.bankBranch ||
    partner.bankRoutingNumber
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm modal-overlay-enter"
      role="dialog"
      aria-modal="true"
      aria-label={`${partner.name} details`}
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl border border-border bg-surface shadow-2xl modal-panel-enter"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b border-border bg-background/50 px-4 py-3 rounded-t-2xl shrink-0">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10">
              <TypeIcon size={18} className="text-primary" />
            </div>
            <div className="min-w-0">
              <h2 className="truncate text-base font-semibold text-text">Referral Partner</h2>
              <p className="truncate text-xs text-text-muted">Profile, earnings &amp; payout history</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 rounded-lg p-1.5 transition-colors hover:bg-background"
          >
            <X size={18} className="text-text-muted" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 space-y-5 overflow-y-auto p-4">
          {/* Profile hero */}
          <div className="flex items-start gap-4 rounded-xl border border-border bg-gradient-to-br from-primary/10 via-surface to-surface p-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary text-lg font-bold text-white shadow-sm">
              {initials(partner.name)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="truncate text-lg font-semibold text-text">{partner.name || '—'}</h3>
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                    isDoctor
                      ? 'bg-primary/10 text-primary'
                      : isEmployee
                        ? 'bg-success/10 text-success'
                        : 'bg-warning/10 text-warning'
                  }`}
                >
                  <TypeIcon size={12} />
                  {typeLabel}
                </span>
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                    partner.isActive ? 'bg-success/10 text-success' : 'bg-background text-text-muted'
                  }`}
                >
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${
                      partner.isActive ? 'bg-success' : 'bg-text-muted'
                    }`}
                  />
                  {partner.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <p className="mt-1 font-mono text-xs text-text-muted">{partner.code || '—'}</p>
              {(partner.organization || partner.designation) && (
                <p className="mt-0.5 truncate text-sm text-text-muted">
                  {[partner.organization, partner.designation].filter(Boolean).join(' · ')}
                </p>
              )}
            </div>
            <div className="shrink-0 text-right">
              <p className="text-[0.6875rem] font-medium uppercase tracking-wide text-text-muted">Default %</p>
              <p className="text-xl font-bold text-primary">
                {Number(partner.defaultSharePercent) || 0}%
              </p>
            </div>
          </div>

          {/* Performance summary */}
          <section>
            <SectionHeader icon={Activity} title="Referral performance" />
            {summaryLoading ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="h-[74px] animate-pulse rounded-xl border border-border bg-background" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <StatTile
                  icon={TrendingUp}
                  label="Lifetime revenue"
                  value={formatCurrency(summary?.lifetimeRevenue)}
                  accent="text-success bg-success/10"
                />
                <StatTile
                  icon={Wallet}
                  label="Earned share"
                  value={formatCurrency(totalEarned)}
                  accent="text-primary bg-primary/10"
                />
                <StatTile
                  icon={Clock}
                  label="Pending"
                  value={formatCurrency(summary?.pendingShare)}
                  accent="text-warning bg-warning/10"
                />
                <StatTile
                  icon={CheckCircle}
                  label="Paid out"
                  value={formatCurrency(summary?.paidAmount)}
                  accent="text-success bg-success/10"
                />
              </div>
            )}
            {!summaryLoading && (
              <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-text-muted">
                <span>
                  <span className="font-medium text-text">{summary?.distributionCount ?? 0}</span> referral
                  {summary?.distributionCount === 1 ? '' : 's'}
                </span>
                <span>
                  <span className="font-medium text-text">{summary?.payoutCount ?? 0}</span> payout
                  {summary?.payoutCount === 1 ? '' : 's'}
                </span>
                <span>
                  Posted (awaiting settlement):{' '}
                  <span className="font-medium text-text">{formatCurrency(summary?.postedShare)}</span>
                </span>
                <span>
                  Settled: <span className="font-medium text-text">{formatCurrency(summary?.settledShare)}</span>
                </span>
              </div>
            )}
          </section>

          {/* Contact */}
          <section>
            <SectionHeader icon={Phone} title="Contact" />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <DetailItem icon={Phone} label="Mobile" value={partner.mobileNumber || '—'} />
              <DetailItem icon={Mail} label="Email" value={partner.email || '—'} />
              <DetailItem
                icon={MapPin}
                label="Address"
                value={partner.address || '—'}
              />
            </div>
          </section>

          {/* Organization / role */}
          <section>
            <SectionHeader
              icon={isDoctor ? Stethoscope : isEmployee ? UserRound : Building2}
              title={isDoctor ? 'Doctor' : isEmployee ? 'Employment' : 'Organization'}
            />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {isDoctor || isEmployee ? (
                <DetailItem
                  icon={isDoctor ? Stethoscope : UserRound}
                  label={isDoctor ? 'Linked doctor' : 'Linked employee'}
                  value={
                    partner.employee
                      ? [partner.employee.firstName, partner.employee.lastName]
                          .filter(Boolean)
                          .join(' ') ||
                        partner.employee.name ||
                        'Linked'
                      : '—'
                  }
                />
              ) : (
                <DetailItem icon={Building2} label="Organization" value={partner.organization || '—'} />
              )}
              <DetailItem icon={Hash} label="Designation" value={partner.designation || '—'} />
              <DetailItem icon={Stethoscope} label="Specialization" value={partner.specialization || '—'} />
            </div>
          </section>

          {/* Bank details */}
          {hasBankDetails && (
            <section>
              <SectionHeader icon={Landmark} title="Bank details" />
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <DetailItem icon={Landmark} label="Bank" value={partner.bankName || '—'} />
                <DetailItem icon={Hash} label="Account number" value={partner.bankAccountNumber || '—'} />
                <DetailItem icon={MapPin} label="Branch" value={partner.bankBranch || '—'} />
                <DetailItem icon={Hash} label="Routing number" value={partner.bankRoutingNumber || '—'} />
              </div>
            </section>
          )}

          {/* Payout history */}
          <section>
            <SectionHeader icon={Receipt} title="Recent payouts" />
            {payoutsLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="h-10 animate-pulse rounded-lg border border-border bg-background" />
                ))}
              </div>
            ) : payouts.length === 0 ? (
              <p className="rounded-lg border border-dashed border-border bg-background px-3 py-4 text-center text-sm text-text-muted">
                No payouts recorded yet.
              </p>
            ) : (
              <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border">
                {payouts.map((payout) => (
                  <li
                    key={payout.id}
                    className="flex items-center justify-between gap-3 bg-background px-3 py-2.5"
                  >
                    <div className="flex min-w-0 items-center gap-2.5">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <Receipt size={14} />
                      </span>
                      <div className="min-w-0">
                        <p className="truncate font-mono text-xs text-text">{payout.paymentNumber || '—'}</p>
                        <p className="truncate text-[0.6875rem] text-text-muted">
                          {payout.method ? `${payout.method} · ` : ''}
                          {formatOrdinalDateTime(payout.paidAt)}
                        </p>
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-sm font-semibold text-text">{formatCurrency(payout.amount)}</p>
                      <span
                        className={`text-[0.625rem] font-medium uppercase ${
                          payout.status === 'PAID' ? 'text-success' : 'text-text-muted'
                        }`}
                      >
                        {payout.status}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {payoutTotal > payouts.length && (
              <p className="mt-2 text-right text-[0.6875rem] text-text-muted">
                Showing latest {payouts.length} of {payoutTotal} payouts
              </p>
            )}
          </section>

          {/* Notes */}
          <section>
            <SectionHeader icon={StickyNote} title="Notes" />
            <p className="whitespace-pre-wrap rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-text">
              {partner.notes || <span className="text-text-muted">No notes provided.</span>}
            </p>
          </section>

          {/* Meta */}
          <section>
            <SectionHeader icon={Calendar} title="Record" />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <DetailItem icon={Calendar} label="Created" value={formatOrdinalDateTime(partner.createdAt)} />
              <DetailItem icon={Calendar} label="Last updated" value={formatOrdinalDateTime(partner.updatedAt)} />
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-border bg-background/50 px-4 py-3 rounded-b-2xl shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="flex items-center gap-2 px-3.5 py-2 text-sm text-text-muted transition-colors hover:text-text"
          >
            <X size={16} />
            Close
          </button>
          {onEdit && (
            <button
              type="button"
              onClick={() => onEdit(partner)}
              className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-dark"
            >
              <Pencil size={16} />
              Edit
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
